import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, IconButton, Snackbar, Typography, useTheme } from "@mui/material";
import { tokens } from "../../theme";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ViewQuiltOutlinedIcon from "@mui/icons-material/ViewQuiltOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import Header from "../../components/Header";
import LineChart from "../../components/LineChart";
import BarChart from "../../components/BarChart";
import StatBox from "../../components/StatBox";
import ProgressCircle from "../../components/ProgressCircle";
import api from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import ReportModal from "../../components/reports/ReportModal";
import {
  formatCurrency,
  generateCorporatePdf,
  generateCsvReport,
  generateExcelReport,
  openPrintReport,
} from "../../utils/reportGenerator";

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalBrands: 0,
    totalLayouts: 0,
    totalUsers: 0,
    revenueGenerated: 0,
    recentActivities: [],
  });
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/stats/dashboard");
        setStats(data);
      } catch {
        /* unauthenticated or API down — keep zeros */
      }
    })();
  }, []);

  const revenueText = useMemo(
    () => formatCurrency(stats.revenueGenerated ?? 0),
    [stats.revenueGenerated]
  );

  const fetchReport = async () => {
    const { data } = await api.get("/api/dashboard/report");
    return data;
  };

  const runReportAction = async (action) => {
    setIsReportModalOpen(false);
    setIsGenerating(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const report = await fetchReport();
      const viewer = report.generatedBy || {
        name: user?.name || "",
        role: user?.role || "",
        email: user?.email || "",
      };
      if (action === "pdf") await generateCorporatePdf(report, viewer);
      else if (action === "xlsx") await generateExcelReport(report, viewer);
      else if (action === "csv") await generateCsvReport(report, viewer);
      else if (action === "print") await openPrintReport(report, viewer);
      setSuccessMessage("Report generated successfully.");
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error.message || "Report generation failed");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Box m="20px">
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="DASHBOARD" subtitle="Welcome to your dashboard" />

        <Box>
          <Button
            onClick={() => setIsReportModalOpen(true)}
            disabled={isGenerating}
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
            }}
          >
            {isGenerating ? (
              <CircularProgress size={16} sx={{ mr: "10px", color: colors.grey[100] }} />
            ) : (
              <DownloadOutlinedIcon sx={{ mr: "10px" }} />
            )}
            {isGenerating ? "Generating report..." : "Download Reports"}
          </Button>
          <ReportModal
            open={isReportModalOpen}
            loading={isGenerating}
            onClose={() => setIsReportModalOpen(false)}
            onSelect={runReportAction}
          />
        </Box>
      </Box>

      {/* GRID & CHARTS */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="140px"
        gap="20px"
      >
        {/* ROW 1 */}
        <Box
          gridColumn="span 4"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={String(stats.totalBrands)}
            subtitle="Total Brands"
            progress="0.6"
            increase="live"
            icon={
              <StorefrontOutlinedIcon
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>
        <Box
          gridColumn="span 4"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={String(stats.totalLayouts)}
            subtitle="Total Layouts"
            progress="0.5"
            increase="live"
            icon={
              <ViewQuiltOutlinedIcon
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>
        <Box
          gridColumn="span 4"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={String(stats.totalUsers)}
            subtitle="Total Users"
            progress="0.4"
            increase="live"
            icon={
              <PeopleOutlinedIcon
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>

        {/* ROW 2 */}
        <Box
          gridColumn="span 8"
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
        >
          <Box
            mt="25px"
            p="0 30px"
            display="flex "
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                color={colors.grey[100]}
              >
                Revenue Generated
              </Typography>
              <Typography
                variant="h3"
                fontWeight="bold"
                color={colors.greenAccent[500]}
              >
                {revenueText}
              </Typography>
            </Box>
            <Box>
              <IconButton>
                <DownloadOutlinedIcon
                  sx={{ fontSize: "26px", color: colors.greenAccent[500] }}
                />
              </IconButton>
            </Box>
          </Box>
          <Box height="250px" m="-20px 0 0 0">
            <LineChart isDashboard={true} />
          </Box>
        </Box>
        <Box
          gridColumn="span 4"
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
          overflow="auto"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={`4px solid ${colors.primary[500]}`}
            colors={colors.grey[100]}
            p="15px"
          >
            <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
              Recent Activities
            </Typography>
          </Box>
          {(stats.recentActivities || []).map((a) => (
            <Box
              key={a.id}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              borderBottom={`4px solid ${colors.primary[500]}`}
              p="15px"
            >
              <Box>
                <Typography
                  color={colors.greenAccent[500]}
                  variant="body2"
                  fontWeight="600"
                >
                  {a.type}
                </Typography>
                <Typography color={colors.grey[100]} variant="body2">
                  {a.message}
                </Typography>
                {a.actor ? (
                  <Typography color={colors.grey[300]} variant="caption">
                    {a.actor.email}
                  </Typography>
                ) : null}
              </Box>
              <Box color={colors.grey[100]} fontSize={12}>
                {a.createdAt ? new Date(a.createdAt).toLocaleString() : ""}
              </Box>
            </Box>
          ))}
        </Box>

        {/* ROW 3 */}
        <Box
          gridColumn="span 4"
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
          p="30px"
        >
          <Typography variant="h5" fontWeight="600">
            Campaign
          </Typography>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            mt="25px"
          >
            <ProgressCircle size="125" />
            <Typography
              variant="h5"
              color={colors.greenAccent[500]}
              sx={{ mt: "15px" }}
            >
              $48,352 revenue generated
            </Typography>
            <Typography>Includes extra misc expenditures and costs</Typography>
          </Box>
        </Box>
        <Box
          gridColumn="span 4"
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
        >
          <Typography
            variant="h5"
            fontWeight="600"
            sx={{ padding: "30px 30px 0 30px" }}
          >
            Sales Quantity
          </Typography>
          <Box height="250px" mt="-20px">
            <BarChart isDashboard={true} />
          </Box>
        </Box>
        <Box
          gridColumn="span 4"
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
          p="30px"
        >
          <Typography
            variant="h5"
            fontWeight="600"
            sx={{ mb: "15px" }}
          >
            Campaign Insights
          </Typography>
          <Typography color={colors.grey[100]}>
            Geographic visualization has been removed from this dashboard.
          </Typography>
        </Box>
      </Box>
      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={5000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={2500}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
